import * as angular from 'angular';

import { IAuthentication, IMediaHelperService, IStoreService } from '../../../services';
import { IRootScope } from '../../../IRootScope';
import * as events from '../../../common/events';
import { ComponentDefinition } from '../../../models';
import { Store, StoreElement } from '../../../stateManagement';

class ShellController implements angular.IComponentController, angular.IOnDestroy {
    authentication: IAuthentication;
    currentStateShowEdit: boolean;
    displaySidebar: boolean;
    isEditMode: boolean;
    isItemSelected: boolean;
    isMaximizedWidget: boolean;
    isMinimized: boolean;
    isMobileMode: boolean;
    isPropertiesMode: boolean;
    isTreePinned: boolean;
    notificationsCount: number;
    widget: any;
    widgetType: string;
    workspaceApplication: string;

    private _unsubscribe: () => void;

    static $inject = ['$scope', '$rootScope', '$state', '$window', 'storeService', 'mediaHelperService'];
    constructor(
        private _$scope: angular.IScope,
        private _$rootScope: IRootScope,
        private _$state: angular.ui.IStateService,
        private _$window: angular.IWindowService,
        private _storeService: IStoreService,
        private _mediaHelperService: IMediaHelperService
    ) {}

    $onInit() {
        const self = this;
        this.currentStateShowEdit = false;
        this.notificationsCount = 0;
        this.isEditMode = false;
        this.isMinimized = false;
        this.isPropertiesMode = false;
        this.isTreePinned = false;

        this._$scope.$on(events.toggleEditMode, self.onToggleEditMode());
        this._$scope.$on(events.toggleMinimizeMenu, self.onToggleMinimizeMenu());
        this._$scope.$on(events.togglePropertiesMode, self.onTogglePropertiesMode());
        this._$scope.$on(events.itemSelectionAllowed, self.onItemSelected());
        this._$scope.$on(events.logout, self.onLogout());
        this._$scope.$on(events.stateChangeSuccess, self.onStateChangeSuccess());
        this._$scope.$on(events.toggleMaximizeWidget, (event, data) => {
            this.isMaximizedWidget = data.isMaximized;
        });
        //TODO remove following method, item-selection-allowed is to be kept
        this._$scope.$on(events.itemSelected, self.onItemSelected());

        this._$scope.$on(events.treePinned, (event, data) => {
            self.isTreePinned = !!data;
        });

        this._$scope.$watch(
            () => this._$rootScope.currentStateShowEdit,
            () => {
                self.currentStateShowEdit = this._$rootScope.currentStateShowEdit;
            }
        );

        this._unsubscribe = this._storeService.register((store) => this.refreshStore(store), [StoreElement.Layout, StoreElement.Authentication]);
        this.setMobileMode(this._mediaHelperService.isMobile());
        this._$window.onresize = (e) => this.setMobileMode(this._mediaHelperService.isMobile());
    }

    $onDestroy(): void {
        if (this._unsubscribe) {
            this._unsubscribe();
        }
    }

    isAuthenticated(): boolean {
        return this.authentication.isAuth;
    }

    isConnected(): boolean {
        return this.authentication.isConnected;
    }

    isAdmin(): boolean {
        return this.authentication.userName === 'Administrator';
    }

    isDemo() {
        let result = false;
        if (this.authentication.isAuth) {
            result = this.authentication.userName === 'demo';
        }
        return result;
    }

    logout(): void {
        this.closeEditMode();
        this.closePropertiesMode();
        this._storeService.dispatchers().authentication.logout();
        this._$state.go('login');
    }

    stateChangeSuccess(): void {
        this.displaySidebar = this.authentication?.isAuth;
    }

    toggleEditMode(): void {
        if (this.isEditMode) {
            this.closeEditMode();
        } else {
            this.openEditMode();
        }
        this.setEditMode(this.isEditMode);
    }

    toggleMinimizeMenu(): void {
        if (this.isMinimized) {
            this.expendMenu();
        } else {
            this.collapseMenu();
        }
    }

    togglePropertiesMode(): void {
        if (this.isPropertiesMode) {
            this.closePropertiesMode();
        } else {
            this.openPropertiesMode();
        }
    }

    mouseenter() {
        this._$rootScope.$broadcast(events.shellContentEnter);
    }

    private refreshStore(store: Store) {
        this.isMobileMode = store.layout.value.isMobileMode;
        this.authentication = store.authentication.value;
        this.stateChangeSuccess();
    }

    private setMobileMode(isMobileMode: boolean): angular.IPromise<void> {
        return this._storeService.dispatchers().layout.setMobileMode(isMobileMode);
    }

    private setEditMode(isEditMode: boolean): angular.IPromise<void> {
        return this._storeService.dispatchers().layout.setEditMode(isEditMode);
    }

    private openEditMode(): void {
        this.isEditMode = true;
        this._$rootScope.isEditMode = true;
        this._$rootScope.$broadcast(events.isEditMode, true);
    }

    private closeEditMode(): void {
        this.isEditMode = false;
        this.closePropertiesMode();
        this._$rootScope.isEditMode = false;
        this._$rootScope.$broadcast(events.isEditMode, false);
    }

    private collapseMenu(): void {
        this.isMinimized = true;
        this._$rootScope.isMinimized = true;
        this._$rootScope.$broadcast(events.isMinimized, true);
    }

    private expendMenu(): void {
        this.isMinimized = false;
        this._$rootScope.isMinimized = false;
        this._$rootScope.$broadcast(events.isMinimized, false);
    }

    private openPropertiesMode(): void {
        this.isPropertiesMode = true;
        this._$rootScope.isPropertiesMode = true;
        this._$rootScope.$broadcast(events.isPropertiesMode, true);
    }

    private closePropertiesMode(): void {
        this.isPropertiesMode = false;
        this._$rootScope.isPropertiesMode = false;
        this._$rootScope.$broadcast(events.isPropertiesMode, false);
    }

    private onToggleEditMode() {
        const self = this;
        return (event, argument) => {
            self.toggleEditMode();
        };
    }

    private onToggleMinimizeMenu() {
        const self = this;
        return (event, argument) => {
            self.toggleMinimizeMenu();
        };
    }

    private onTogglePropertiesMode() {
        const self = this;
        return (event, argument) => {
            self.togglePropertiesMode();
        };
    }

    private onItemSelected() {
        const self = this;
        return (event, argument) => {
            if (!argument) {
                self.isItemSelected = false;
            } else {
                self.widget = argument;
                self.widgetType = argument.type;
                self.isItemSelected = true;
            }
        };
    }

    private onLogout() {
        const self = this;
        return (event, argument) => {
            self.logout();
        };
    }

    private onStateChangeSuccess() {
        const self = this;
        return (event, argument) => {
            self.stateChangeSuccess();
        };
    }
}

export const wtShell: ComponentDefinition = {
    componentId: 'wtShell',
    controller: ShellController,
    controllerAs: 'vm',
    template: require('./shell.component.html'),
    bindings: {}
};
